<template>
    <div class="bill-list-compact-container">
        <div class="bill-list-compact-header">
            <h3 class="compact-title">청구 정보</h3>
            <button class="manage-button" @click="openManageModal" title="고객 관리">
                <i class="material-icons">more_vert</i>
            </button>
        </div>
        <div class="bill-list-compact-content">
            <table class="bill-table">
                <thead>
                    <tr>
                        <th class="col-customer">고객명 <span class="header-subtitle">(요금제)</span></th>
                        <th class="col-power">계약전력</th>
                        <th class="col-amount">총 청구 금액</th>
                        <th class="col-usage">총 사용량</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(bill, index) in billList"
                        :key="bill.custNo || index"
                        class="bill-row"
                        @click="handleRowClick(bill)"
                    >
                        <td class="col-customer">
                            <div class="customer-info-container">
                                <div class="customer-primary-line">
                                    <span class="customer-name-inline">{{ bill.custNm || "-" }}</span>
                                    <span class="customer-status-inline" :class="getStatusClass(bill.status)">{{
                                        getStatusText(bill.status)
                                    }}</span>
                                </div>
                                <div class="customer-secondary-line">
                                    <span class="customer-number-inline">{{ bill.custNo }}</span>
                                    <span class="line-separator">|</span>
                                    <span class="customer-contract-inline">{{ bill.contractInfo?.ictg || "-" }}</span>
                                </div>
                            </div>
                        </td>
                        <td class="col-power">
                            <div class="power-value">
                                {{ formatContractPower(bill.contractInfo?.ikw) }}<span class="value-unit">kW</span>
                            </div>
                        </td>
                        <td class="col-amount">
                            <div class="amount-value primary">
                                {{ formatCurrency(bill.totalAmount) }}<span class="value-unit">원</span>
                            </div>
                        </td>
                        <td class="col-usage">
                            <div class="usage-value">
                                {{ formatUsage(bill.totalUsage)
                                }}<span class="value-unit">{{ getUsageUnit(bill.totalUsage) }}</span>
                            </div>
                        </td>
                    </tr>
                    <!-- 요약 행 -->
                    <tr v-if="billList && billList.length > 0" class="summary-row">
                        <td class="col-customer">
                            <div class="summary-label">합계</div>
                        </td>
                        <td class="col-power"></td>
                        <td class="col-amount">
                            <div class="amount-value primary">
                                {{ formatCurrency(summaryTotal.totalAmount) }}<span class="value-unit">원</span>
                            </div>
                        </td>
                        <td class="col-usage">
                            <div class="usage-value">
                                {{ formatUsage(summaryTotal.totalUsage)
                                }}<span class="value-unit">{{ getUsageUnit(summaryTotal.totalUsage) }}</span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- 고객 관리 모달 -->
        <CustomerManagementModal v-model="isManageModalOpen" @customer-updated="handleCustomerUpdated" />
    </div>
</template>

<script>
import { formatEnergyUsage } from "../../utils/energyUtils.js";
import { getPrimaryBlueColor } from "../../utils/colorUtils.js";
import CustomerManagementModal from "./CustomerManagementModal.vue";
import "material-icons/iconfont/material-icons.css";

export default {
    name: "DashboardBillListCompact",
    components: {
        CustomerManagementModal,
    },
    props: {
        billList: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isManageModalOpen: false,
        };
    },
    computed: {
        summaryTotal() {
            if (!this.billList || this.billList.length === 0) {
                return {
                    totalAmount: 0,
                    totalUsage: 0,
                };
            }

            return this.billList.reduce(
                (acc, bill) => {
                    acc.totalAmount += Number(bill.totalAmount || 0);
                    acc.totalUsage += Number(bill.totalUsage || 0);
                    return acc;
                },
                { totalAmount: 0, totalUsage: 0 }
            );
        },
        primaryBlueColor() {
            return getPrimaryBlueColor();
        },
    },
    methods: {
        formatCurrency(value) {
            const num = Number(value);
            if (isNaN(num) || num === 0) return "-";
            return num.toLocaleString();
        },
        formatUsage(value) {
            return formatEnergyUsage(value, { showUnit: false });
        },
        getUsageUnit(value) {
            const num = Number(value);
            if (isNaN(num) || num === 0) return "";
            if (num >= 1000000) return "GWh";
            if (num >= 1000) return "MWh";
            return "kWh";
        },
        formatContractPower(value) {
            const num = Number(value);
            if (isNaN(num) || num === 0) return "-";
            return num.toLocaleString();
        },
        handleRowClick(bill) {
            this.$emit("select-customer", bill.custNo);
        },
        openManageModal() {
            this.isManageModalOpen = true;
        },
        handleCustomerUpdated() {
            // 부모 컴포넌트에게 고객 목록이 업데이트되었음을 알림
            this.$emit("customers-updated");
        },
        getStatusClass(status) {
            // status에 따른 클래스 반환
            if (status === "pending") return "status-pending";
            if (status === "predict") return "status-predict";
            return "status-active";
        },
        getStatusText(status) {
            const statusMap = {
                pending: "대기",
                predict: "예측",
                active: "청구",
            };
            return statusMap[status] || "";
        },
    },
};
</script>

<style scoped>
button:focus {
    outline: none;
}
.bill-list-compact-container {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.bill-list-compact-header {
    padding: 12px 16px;
    flex-shrink: 0;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.compact-title {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

.manage-button {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    background: #ffffff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.manage-button:hover {
    color: #475569;
}

.manage-button .material-icons {
    font-size: 18px;
    flex-shrink: 0;
}

.bill-list-compact-content {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
}

.bill-list-compact-content::-webkit-scrollbar {
    width: 6px;
}

.bill-list-compact-content::-webkit-scrollbar-track {
    background: transparent;
}

.bill-list-compact-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.bill-list-compact-content::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* 테이블 스타일 */
.bill-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background: #ffffff;
}

.bill-table thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #f8fafc;
}

.bill-table th {
    padding: 10px 12px;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    text-align: left;
    border-bottom: 2px solid #e2e8f0;
    background: #f8fafc;
}

.header-subtitle {
    font-size: 10px;
    font-weight: 500;
    color: #94a3b8;
    text-transform: none;
    letter-spacing: 0;
}

.header-unit {
    font-size: 9px;
    font-weight: 400;
    color: #cbd5e1;
    text-transform: none;
    letter-spacing: 0;
    margin-left: 3px;
}

.bill-table th.col-customer {
    width: 40%;
}

.bill-table th.col-power {
    width: 15%;
    text-align: right;
}

.bill-table th.col-amount {
    width: 25%;
    text-align: right;
}

.bill-table th.col-usage {
    width: 20%;
    text-align: right;
}

.bill-table td {
    padding: 6px 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

.bill-table th.col-power,
.bill-table th.col-amount,
.bill-table td.col-power,
.bill-table td.col-amount {
    padding-left: 0;
    padding-right: 0;
}

.bill-table tbody tr.bill-row {
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.bill-table tbody tr.bill-row:hover {
    background: #f8fafc;
}

.bill-table tbody tr.bill-row:active {
    background: #f1f5f9;
}

/* 고객 정보 컨테이너 */
.customer-info-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

/* 첫 번째 줄: 고객명 + 상태 */
.customer-primary-line {
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1.3;
}

.customer-name-inline {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
}

.customer-status-inline {
    font-size: 9px;
    font-weight: 600;
    color: #64748b;
    padding: 2px 5px;
    border-radius: 3px;
    background: #f1f5f9;
    letter-spacing: 0.2px;
}

/* 상태별 배경색 구분 - 그레이 톤 */
.customer-status-inline.status-active {
    background: #e2e8f0;
}

.customer-status-inline.status-pending {
    background: #cbd5e1;
}

.customer-status-inline.status-predict {
    background: #94a3b8;
    color: #ffffff;
}

/* 두 번째 줄: 고객번호 + 요금제 */
.customer-secondary-line {
    display: flex;
    align-items: center;
    line-height: 1.2;
}

.customer-number-inline {
    font-size: 10px;
    font-weight: 500;
    color: #64748b;
}

.line-separator {
    font-size: 9px;
    color: #94a3b8;
    font-weight: 400;
    margin: 0 5px;
}

.customer-contract-inline {
    font-size: 10px;
    font-weight: 500;
    color: #64748b;
}

.col-amount {
    text-align: right;
}

.amount-value {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.4;
}

.amount-value.primary {
    color: v-bind("primaryBlueColor");
}

.col-power {
    text-align: right;
}

.power-value {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    line-height: 1.4;
}

.col-usage {
    text-align: right;
}

.usage-value {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    line-height: 1.4;
}

.value-unit {
    font-size: 9px;
    font-weight: 400;
    color: #cbd5e1;
    margin-left: 3px;
}

/* 요약 행 */
.summary-row {
    background: #f8fafc;
    position: sticky;
    bottom: 0;
}

.summary-row td {
    border-bottom: none;
    padding: 12px 12px;
}

.summary-label {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
}

.summary-row .amount-value {
    font-size: 13px;
    font-weight: 800;
}

.summary-row .usage-value {
    font-size: 13px;
    font-weight: 800;
}
</style>
